import type { DependencyList } from 'react'
import { useCallback, useRef, useState } from 'react'
import type { AnyAsyncFn } from './type'

const useLoadingCallback = <T extends AnyAsyncFn>(
  cb: T,
  deps: DependencyList = [],
  opts?: {
    defaultLoadingState?: boolean
  },
) => {
  const [loading, setLoading] = useState(opts?.defaultLoadingState ?? false)
  const loadingRef = useRef(false)
  const pendingRef = useRef<
    { fn: T; args: Parameters<T>; thisArg: any } | undefined
  >(undefined)
  let resolvers: [
    (value: Awaited<ReturnType<T>>) => void,
    (reason?: any) => void,
  ][] = []

  const loadingCallback = useCallback(async (...args: Parameters<T>) => {
    if (loadingRef.current) {
      pendingRef.current = { fn: loadingCallback, args, thisArg: this }
      return new Promise<ReturnType<T>>((resolve, reject) => {
        resolvers.push([resolve, reject])
      })
    }

    loadingRef.current = true
    setLoading(true)
    try {
      return await cb(...args)
    } finally {
      loadingRef.current = false
      if (pendingRef.current) {
        const p = pendingRef.current,
          r = resolvers
        pendingRef.current = undefined
        resolvers = []
        Promise.resolve().then(() => {
          p.fn
            .apply(p.thisArg, p.args)
            .then(result => {
              r.forEach(([resolve]) => Promise.resolve(result).then(resolve))
            })
            .catch(error => {
              r.forEach(([, reject]) => Promise.resolve(error).then(reject))
            })
        })
      } else {
        setLoading(false)
      }
    }
  }, deps) as T

  return [loading, loadingCallback] as const
}

export default useLoadingCallback
